import './ConversationHistory.scss';

import Box from "@amzn/awsui-components-console/box";
import List from "@amzn/awsui-components-console/list";
import Badge from "@amzn/awsui-components-react/polaris/badge";
import Button from "@amzn/awsui-components-react/polaris/button";
import ButtonDropdown from "@amzn/awsui-components-react/polaris/button-dropdown";
import Link from "@amzn/awsui-components-react/polaris/link";
import Modal from "@amzn/awsui-components-react/polaris/modal";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Spinner from "@amzn/awsui-components-react/polaris/spinner";
import { useState } from "react";

import { getString } from "../../../common/ui-string-labels/ui-strings-utils";
import { ConversationSummary } from "../../../services/chatbot-service";

export interface ConversationHistoryProps {
    conversations: ConversationSummary[];
    activeConversationId: string | null;
    loading: boolean;
    hasMore: boolean;
    disabled?: boolean;
    pendingConversationIds?: string[];
    unreadConversationIds?: string[];
    onSelect: (id: string) => void;
    onDelete: (id: string) => void;
    onNewChat: () => void;
    onLoadMore: () => void;
    onClose?: () => void;
}

interface TimeGroup {
    label: string;
    conversations: ConversationSummary[];
}

const groupConversationsByTime = (conversations: ConversationSummary[]): TimeGroup[] => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfYesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).getTime();
    const startOfPast7Days = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7).getTime();

    const today: ConversationSummary[] = [];
    const yesterday: ConversationSummary[] = [];
    const past7Days: ConversationSummary[] = [];
    const older: ConversationSummary[] = [];

    for (const conv of conversations) {
        if (conv.updatedAt >= startOfToday) {
            today.push(conv);
        } else if (conv.updatedAt >= startOfYesterday) {
            yesterday.push(conv);
        } else if (conv.updatedAt >= startOfPast7Days) {
            past7Days.push(conv);
        } else {
            older.push(conv);
        }
    }

    const groups: TimeGroup[] = [];
    if (today.length > 0) groups.push({ label: getString('chatbot.history.today'), conversations: today });
    if (yesterday.length > 0) groups.push({ label: getString('chatbot.history.yesterday'), conversations: yesterday });
    if (past7Days.length > 0) groups.push({ label: getString('chatbot.history.past7Days'), conversations: past7Days });
    if (older.length > 0) groups.push({ label: getString('chatbot.history.older'), conversations: older });

    return groups;
};

const ConversationHistory = ({
    conversations,
    activeConversationId,
    loading,
    hasMore,
    disabled = false,
    pendingConversationIds = [],
    unreadConversationIds = [],
    onSelect,
    onDelete,
    onNewChat,
    onLoadMore,
    onClose,
}: ConversationHistoryProps) => {
    const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
    const handleConfirmDelete = () => {
        if (deleteTargetId) {
            onDelete(deleteTargetId);
            setDeleteTargetId(null);
        }
    };

    const handleCancelDelete = () => {
        setDeleteTargetId(null);
    };

    const groups = groupConversationsByTime(conversations);
    const isEmpty = conversations.length === 0;
    const showListSpinner = loading && conversations.length === 0;

    return (
        <div className="conversation-history">
            {/* Fixed header */}
            <div className="conversation-history__header">
                <SpaceBetween size="s">
                    <div className="conversation-history__header-row">
                        <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                            <Box variant="h3">{getString('chatbot.history.title')}</Box>
                            <Badge>{conversations.length.toString()}</Badge>
                        </SpaceBetween>
                        {onClose && (
                            <Button
                                variant="icon"
                                iconName="angle-left"
                                onClick={onClose}
                                ariaLabel={getString('chatbot.history.toggleHistory')}
                            />
                        )}
                    </div>

                    <Button
                        data-testid="chatbot-history-new-chat"
                        variant="normal"
                        iconName="add-plus"
                        onClick={onNewChat}
                        disabled={disabled}
                        fullWidth
                    >
                        {getString('chatbot.history.newChat')}
                    </Button>
                </SpaceBetween>
            </div>

            {/* Scrollable list */}
            <div className="conversation-history__list">
                <SpaceBetween size="s">
                    {showListSpinner && (
                        <Box textAlign="center" padding="l">
                            <Spinner />
                        </Box>
                    )}

                    {!showListSpinner && isEmpty && (
                        <Box textAlign="center" color="text-body-secondary" padding="l">
                            {getString('chatbot.history.empty')}
                        </Box>
                    )}

                    {groups.map((group) => (
                        <SpaceBetween size="xxs" key={group.label}>
                            <Box variant="h5" color="text-body-secondary">
                                {group.label}
                            </Box>
                            <div className="conversation-history__group">
                                <List
                                    items={group.conversations}
                                    tagOverride="ul"
                                    ariaLabel={group.label}
                                    renderItem={(conv) => {
                                        const title = conv.title || getString('chatbot.history.untitled');
                                        const isActive = conv.conversationId === activeConversationId;
                                        const isPending = pendingConversationIds.includes(conv.conversationId);
                                        const isUnread = !isPending && unreadConversationIds.includes(conv.conversationId);
                                        const statusLabel = isPending
                                            ? getString('chatbot.history.waitingForAnswerLabel')
                                            : isUnread ? getString('chatbot.history.newAnswerLabel') : undefined;
                                        return {
                                            id: conv.conversationId,
                                            announcementLabel: statusLabel ? `${title}, ${statusLabel}` : title,
                                            content: (
                                                <button
                                                    type="button"
                                                    data-testid="chatbot-history-item"
                                                    className="conversation-history__item-button"
                                                    title={title}
                                                    disabled={disabled}
                                                    aria-current={isActive ? 'true' : undefined}
                                                    onClick={() => onSelect(conv.conversationId)}
                                                >
                                                    {title}
                                                </button>
                                            ),
                                            actions: (
                                                <div className="conversation-history__item-actions">
                                                {isPending && <Spinner data-testid="chatbot-history-item-pending" />}
                                                {isUnread && (
                                                    <span data-testid="chatbot-history-item-new">
                                                        <Badge color="blue">{getString('chatbot.history.newAnswer')}</Badge>
                                                    </span>
                                                )}
                                                <ButtonDropdown
                                                    variant="icon"
                                                    disabled={disabled || isPending}
                                                    items={[
                                                        { id: "delete", text: getString('chatbot.history.deleteChat') },
                                                    ]}
                                                    onItemClick={({ detail }) => {
                                                        if (detail.id === "delete") {
                                                            setDeleteTargetId(conv.conversationId);
                                                        }
                                                    }}
                                                    ariaLabel={getString('chatbot.history.moreActions')}
                                                />
                                                </div>
                                            ),
                                        };
                                    }}
                                />
                            </div>
                        </SpaceBetween>
                    ))}

                    {hasMore && (
                        <Box textAlign="center">
                            <Link onFollow={onLoadMore}>
                                {getString('chatbot.history.viewMore')}
                            </Link>
                        </Box>
                    )}

                    {loading && conversations.length > 0 && (
                        <Box textAlign="center">
                            <Spinner />
                        </Box>
                    )}

                    <Box textAlign="center" color="text-body-secondary" fontSize="body-s">
                        {getString('chatbot.history.retentionNote')}
                    </Box>
                </SpaceBetween>
            </div>

            <Modal
                visible={deleteTargetId !== null}
                onDismiss={handleCancelDelete}
                header={getString('chatbot.history.deleteConfirmTitle')}
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={handleCancelDelete}>
                                {getString('common.cancel')}
                            </Button>
                            <Button variant="primary" onClick={handleConfirmDelete} data-testid="confirm-delete-btn">
                                {getString('common.delete')}
                            </Button>
                        </SpaceBetween>
                    </Box>
                }
            >
                {getString('chatbot.history.deleteConfirmMessage')}
            </Modal>
        </div>
    );
};

export default ConversationHistory;
